import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Vérifier l'authentification au chargement
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      
      // Vérifier si l'utilisateur est authentifié via Cloudflare Access
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Mode développement : simuler un utilisateur connecté
        if (import.meta.env.DEV) {
          setUser({
            id: 'dev-user',
            email: 'dev@exemple.com',
            name: 'Utilisateur Démo',
            picture: '',
          });
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      // Mode développement : simuler un utilisateur connecté
      if (import.meta.env.DEV) {
        setUser({
          id: 'dev-user',
          email: 'dev@exemple.com',
          name: 'Utilisateur Démo',
          picture: '',
        });
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = () => {
    // Rediriger vers Cloudflare Access pour l'authentification
    const cloudflareDomain = import.meta.env.VITE_CLOUDFLARE_ACCESS_DOMAIN;
    const currentUrl = encodeURIComponent(window.location.href);
    window.location.href = `${cloudflareDomain}/auth?redirect=${currentUrl}`;
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}; 